import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  getLandingReport,
  type LandingDailyPoint,
  type LandingVariantKey,
  type LandingVariantTotals,
} from "@/lib/admin.landing.functions";

export const Route = createFileRoute("/admin/landing")({
  head: () => ({
    meta: [
      { title: "Landing A/B — Administrator" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: LandingAnalyticsPage,
});

function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

const A_KEY: LandingVariantKey = "home-v4";
const B_KEY: LandingVariantKey = "home-v5";
const A_LABEL = "/home-v4";
const B_LABEL = "/home-v5";

function LandingAnalyticsPage() {
  const [rangeDays, setRangeDays] = useState(30);
  const fn = useServerFn(getLandingReport);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "landing-report", rangeDays],
    queryFn: () => fn({ data: { rangeDays } }),
  });

  return (
    <div className="space-y-10">
      <section>
        <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
          <div>
            <p className="overline mb-1">Landing A/B</p>
            <h2 className="font-serif text-2xl text-primary">
              Choice A ({A_LABEL}) vs Choice B ({B_LABEL})
            </h2>
            <p className="text-sm text-ink-tertiary mt-1">
              Page views, CTA clicks, and sign-up conversions per variant.
              Visitors landing on <span className="font-mono">/</span> are split 50/50.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <label className="text-ink-tertiary">Range</label>
            <select
              className="border border-border bg-card px-2 py-1 font-mono"
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
            >
              <option value={7}>7d</option>
              <option value={14}>14d</option>
              <option value={30}>30d</option>
              <option value={60}>60d</option>
              <option value={90}>90d</option>
            </select>
            <button
              onClick={() => refetch()}
              className="px-3 py-1 border border-border hover:bg-muted"
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
        {error && (
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : "Failed to load report"}
          </p>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <VariantCard label={`Choice A · ${A_LABEL}`} variant={A_KEY} t={data.totals[A_KEY]} />
              <VariantCard label={`Choice B · ${B_LABEL}`} variant={B_KEY} t={data.totals[B_KEY]} />
            </div>

            <ComparisonRow data={data.totals} />

            <h3 className="font-serif text-xl text-primary mt-10 mb-3">
              Daily breakdown ({data.rangeDays} days)
            </h3>
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm font-mono">
                <thead className="bg-muted/50 text-xs">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-right p-2" colSpan={3}>Choice A · {A_LABEL}</th>
                    <th className="text-right p-2" colSpan={3}>Choice B · {B_LABEL}</th>
                  </tr>
                  <tr className="text-ink-tertiary">
                    <th></th>
                    <th className="text-right p-2 font-normal">views</th>
                    <th className="text-right p-2 font-normal">clicks</th>
                    <th className="text-right p-2 font-normal">signups</th>
                    <th className="text-right p-2 font-normal">views</th>
                    <th className="text-right p-2 font-normal">clicks</th>
                    <th className="text-right p-2 font-normal">signups</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((d) => (
                    <tr key={d.date} className="border-t border-border">
                      <td className="p-2">{d.date}</td>
                      <td className="text-right p-2">{d.a_views}</td>
                      <td className="text-right p-2">{d.a_clicks}</td>
                      <td className="text-right p-2 text-primary">{d.a_signups}</td>
                      <td className="text-right p-2">{d.b_views}</td>
                      <td className="text-right p-2">{d.b_clicks}</td>
                      <td className="text-right p-2 text-primary">{d.b_signups}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DailyChart daily={data.daily} />

            <p className="text-xs text-ink-tertiary mt-6 font-mono">
              Generated {new Date(data.generated_at).toLocaleString()}
            </p>
          </>
        )}
      </section>
    </div>
  );
}

function VariantCard({
  label,
  variant,
  t,
}: {
  label: string;
  variant: string;
  t: LandingVariantTotals;
}) {
  return (
    <div className="border border-border bg-card p-6">
      <div className="flex items-baseline justify-between mb-4">
        <p className="font-mono text-xs text-ink-tertiary">{variant}</p>
        <p className="font-mono text-sm text-primary">{label}</p>
      </div>
      <dl className="grid grid-cols-3 gap-4 mb-4">
        <Stat label="Views" value={t.views} />
        <Stat label="CTA clicks" value={t.clicks} />
        <Stat label="Sign-ups" value={t.signups} />
      </dl>
      <dl className="grid grid-cols-3 gap-4 border-t border-border pt-4">
        <Stat label="Sessions" value={t.uniqueSessions} muted />
        <Stat label="CTA rate" value={pct(t.ctaRate)} muted />
        <Stat label="Signup rate" value={pct(t.signupRate)} muted highlight />
      </dl>
    </div>
  );
}

function Stat({
  label,
  value,
  muted,
  highlight,
}: {
  label: string;
  value: number | string;
  muted?: boolean;
  highlight?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-ink-tertiary uppercase tracking-wide">{label}</dt>
      <dd
        className={
          "font-serif mt-1 " +
          (highlight ? "text-2xl text-primary" : muted ? "text-lg text-ink-secondary" : "text-2xl text-primary")
        }
      >
        {value}
      </dd>
    </div>
  );
}

function ComparisonRow({
  data,
}: {
  data: Record<LandingVariantKey, LandingVariantTotals>;
}) {
  const a = data[A_KEY];
  const b = data[B_KEY];
  const lift =
    a.signupRate > 0 ? ((b.signupRate - a.signupRate) / a.signupRate) * 100 : null;
  const winner =
    a.signupRate === b.signupRate
      ? "Tied"
      : b.signupRate > a.signupRate
      ? `Choice B (${B_LABEL})`
      : `Choice A (${A_LABEL})`;
  return (
    <div className="border border-border bg-muted/30 p-6">
      <p className="overline mb-2">Head-to-head</p>
      <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
        <div>
          <span className="text-ink-tertiary">Leading variant: </span>
          <span className="font-mono text-primary">{winner}</span>
        </div>
        <div>
          <span className="text-ink-tertiary">Signup-rate lift (B vs A): </span>
          <span className="font-mono text-primary">
            {lift === null ? "n/a" : `${lift >= 0 ? "+" : ""}${lift.toFixed(1)}%`}
          </span>
        </div>
      </div>
    </div>
  );
}

function DailyChart({ daily }: { daily: LandingDailyPoint[] }) {
  const max = Math.max(
    1,
    ...daily.map((d) =>
      Math.max(d.a_views, d.b_views, d.a_clicks, d.b_clicks),
    ),
  );
  return (
    <div className="mt-8 border border-border bg-card p-4">
      <p className="overline mb-3">Page views (per day)</p>
      <div className="flex items-end gap-1 h-32">
        {daily.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-px" title={d.date}>
            <div className="w-full flex gap-px items-end h-full">
              <div
                className="flex-1 bg-primary/40"
                style={{ height: `${(d.a_views / max) * 100}%` }}
                title={`A (${A_LABEL}): ${d.a_views}`}
              />
              <div
                className="flex-1 bg-primary"
                style={{ height: `${(d.b_views / max) * 100}%` }}
                title={`B (${B_LABEL}): ${d.b_views}`}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 text-xs font-mono text-ink-tertiary mt-3">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 bg-primary/40" /> Choice A · {A_LABEL}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 bg-primary" /> Choice B · {B_LABEL}
        </span>
      </div>
    </div>
  );
}
